<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import whatsappCallingClient, {
  WHATSAPP_CALL_AUDIO_EVENTS,
} from 'dashboard/helper/whatsappCallingClient';
import {
  MESSAGE_TYPES,
  VOICE_CALL_STATUS,
} from 'dashboard/components-next/message/constants';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const actionStatus = ref(null);
const actionCallId = ref(null);
const isBusy = ref(false);
const audioBlockedCallId = ref(null);

const activeCallMessage = computed(() => {
  if (!props.chat?.messages) return null;
  // Find the most recent voice call message
  const callMessages = props.chat.messages.filter(
    m => m.content_type === 'voice_call'
  );
  if (!callMessages.length) return null;

  const latestCall = callMessages[callMessages.length - 1];
  const callId = latestCall.content_attributes?.call_id;
  const rawStatus = latestCall.content_attributes?.data?.status;
  const status =
    actionCallId.value === callId && actionStatus.value
      ? actionStatus.value
      : rawStatus;

  if (
    status === VOICE_CALL_STATUS.RINGING ||
    status === VOICE_CALL_STATUS.IN_PROGRESS
  ) {
    return { ...latestCall, status, callId };
  }
  return null;
});

const isRinging = computed(
  () => activeCallMessage.value?.status === VOICE_CALL_STATUS.RINGING
);
const isInProgress = computed(
  () => activeCallMessage.value?.status === VOICE_CALL_STATUS.IN_PROGRESS
);
const isIncoming = computed(
  () =>
    activeCallMessage.value?.message_type === MESSAGE_TYPES.INCOMING ||
    activeCallMessage.value?.message_type === 'incoming'
);
const inboxId = computed(() => props.chat?.inbox_id || props.chat?.inbox?.id);
const remoteSdp = computed(
  () =>
    activeCallMessage.value?.content_attributes?.remote_sdp ||
    activeCallMessage.value?.content_attributes?.data?.remote_sdp
);
const canAccept = computed(
  () => isRinging.value && isIncoming.value && !!remoteSdp.value
);
const bannerIcon = computed(() => {
  if (!isRinging.value) return 'i-ph-phone-call';
  return isIncoming.value ? 'i-ph-phone-incoming' : 'i-ph-phone-outgoing';
});
const shouldShowEnableAudio = computed(
  () => activeCallMessage.value?.callId === audioBlockedCallId.value
);

const bannerText = computed(() => {
  if (isRinging.value) {
    return isIncoming.value
      ? t('CONVERSATION.VOICE_CALL.INCOMING_CALL')
      : t('CONVERSATION.VOICE_CALL.OUTGOING_CALL');
  }
  return t('CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS');
});

const setLocalStatus = status => {
  actionCallId.value = activeCallMessage.value?.callId;
  actionStatus.value = status;
};

const acceptCall = async () => {
  if (!activeCallMessage.value?.callId || !inboxId.value) return;
  isBusy.value = true;
  try {
    await whatsappCallingClient.acceptInboundCall({
      inboxId: inboxId.value,
      callId: activeCallMessage.value.callId,
      remoteSdp: remoteSdp.value,
      bizOpaqueCallbackData: props.chat.id,
    });
    setLocalStatus(VOICE_CALL_STATUS.IN_PROGRESS);
  } finally {
    isBusy.value = false;
  }
};

const rejectCall = async () => {
  if (!activeCallMessage.value?.callId || !inboxId.value) return;
  isBusy.value = true;
  try {
    await whatsappCallingClient.rejectCall({
      inboxId: inboxId.value,
      callId: activeCallMessage.value.callId,
    });
    setLocalStatus(VOICE_CALL_STATUS.NO_ANSWER);
  } finally {
    isBusy.value = false;
  }
};

const terminateCall = async () => {
  if (!activeCallMessage.value?.callId || !inboxId.value) return;
  isBusy.value = true;
  try {
    await whatsappCallingClient.terminateCall({
      inboxId: inboxId.value,
      callId: activeCallMessage.value.callId,
    });
    setLocalStatus(VOICE_CALL_STATUS.COMPLETED);
  } finally {
    isBusy.value = false;
  }
};

const enableAudio = async () => {
  if (!activeCallMessage.value?.callId) return;

  await whatsappCallingClient.resumeRemoteAudio(activeCallMessage.value.callId);
  audioBlockedCallId.value = null;
};

const handleAudioBlocked = event => {
  audioBlockedCallId.value = event.detail?.callId;
};

onMounted(() => {
  window.addEventListener(
    WHATSAPP_CALL_AUDIO_EVENTS.BLOCKED,
    handleAudioBlocked
  );
});

onUnmounted(() => {
  window.removeEventListener(
    WHATSAPP_CALL_AUDIO_EVENTS.BLOCKED,
    handleAudioBlocked
  );
});
</script>

<template>
  <div>
    <div
      v-if="activeCallMessage"
      class="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white transition-colors duration-300"
      :class="isRinging ? 'bg-n-teal-9 animate-pulse' : 'bg-n-teal-9'"
    >
      <Icon :icon="bannerIcon" class="mr-2 size-4" />
      <span class="mr-3">{{ bannerText }}</span>
      <div class="flex items-center gap-2">
        <button
          v-if="shouldShowEnableAudio"
          type="button"
          class="inline-flex gap-1 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 px-2 h-7 disabled:opacity-50"
          :aria-label="t('CONVERSATION.VOICE_WIDGET.ENABLE_AUDIO')"
          :title="t('CONVERSATION.VOICE_WIDGET.ENABLE_AUDIO')"
          :disabled="isBusy"
          @click="enableAudio"
        >
          <Icon icon="i-lucide-volume-2" class="size-4" />
          <span class="text-xs">
            {{ t('CONVERSATION.VOICE_WIDGET.ENABLE_AUDIO') }}
          </span>
        </button>
        <button
          v-if="isRinging && isIncoming"
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 size-7 disabled:opacity-50"
          :aria-label="t('CONVERSATION.VOICE_WIDGET.JOIN_CALL')"
          :title="t('CONVERSATION.VOICE_WIDGET.JOIN_CALL')"
          :disabled="isBusy || !canAccept"
          @click="acceptCall"
        >
          <Icon icon="i-ph-phone-call" class="size-4" />
        </button>
        <button
          v-if="isRinging && isIncoming"
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 size-7 disabled:opacity-50"
          :aria-label="t('CONVERSATION.VOICE_WIDGET.REJECT_CALL')"
          :title="t('CONVERSATION.VOICE_WIDGET.REJECT_CALL')"
          :disabled="isBusy"
          @click="rejectCall"
        >
          <Icon icon="i-ph-phone-x" class="size-4" />
        </button>
        <button
          v-if="isInProgress || (isRinging && !isIncoming)"
          type="button"
          class="inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 size-7 disabled:opacity-50"
          :aria-label="t('CONVERSATION.VOICE_WIDGET.END_CALL')"
          :title="t('CONVERSATION.VOICE_WIDGET.END_CALL')"
          :disabled="isBusy"
          @click="terminateCall"
        >
          <Icon icon="i-ph-phone-disconnect" class="size-4" />
        </button>
      </div>
    </div>
  </div>
</template>
